import { ThunkDispatch } from 'redux-thunk';

import { IAutomatedActionAudit } from '@missionlabs/smartagent-service-thread/dist/entities/action-audit';
import { IContactAudit } from '@missionlabs/smartagent-service-contact/dist/lib/entities/contact-audit';
import { SocialChatJsonMessage } from '@missionlabs/smartagent-service-chat/dist/types/socialChatJsonMessage';
import { SAMessageType } from '@missionlabs/smartagent-chat-components-lib/dist/ChatMessage/chat-message.types';

import { getContacts, getContact, getChatTranscript, getContactAudit, getContactsById, getEventTraceRecords } from 'services/api/api.contact';
import { getActionAudit } from 'services/api/api.automated-rules';
import { getDownloadURL } from 'services/api/api.thread';
import RootState from 'store/state';
import ContactState from 'store/contact/contact.state';
import { paramsToQueryString } from 'utils';

import ContactHistoryTypes from './contactHistory.types';
import { socialChannels } from './contactHistory.constants';
import { ChannelType, ContactHistory, IInteraction, InteractionMessage } from 'views/Metrics/HistoricMetrics/types';
import { IEmailAttachment } from 'components/EmailMessage/types';
import { Sort } from './contactHistory.state';
import { transformTaskContacts, transformContacts, getThreadMessageGroups, mergeAndMapData } from 'views/Metrics/HistoricMetrics/utils';
import {
    transformMessageContent,
    constructQueryStringByChannel,
    getContactHistoryIdsFromOtherParties,
    sortContactHistoryInteractions,
} from './contactHistory.utils';

export type ContactHistoryAction =
    | {
          type: typeof ContactHistoryTypes.ADD_CONTACT_HISTORY;
          payload: {
              attributeSearchName: string;
              attributeSearchValue: string;
              interactions: IInteraction[];
              displayLabel?: string;
          };
      }
    | {
          type: typeof ContactHistoryTypes.REMOVE_CONTACT_HISTORY;
          payload: { attributeSearchName: string; attributeSearchValue: string };
      }
    | {
          type: typeof ContactHistoryTypes.SET_CH_INTERACTIONS;
          payload: { attributeSearchName: string; attributeSearchValue: string; interactions: ContactHistory };
      }
    | {
          type: typeof ContactHistoryTypes.SET_CH_SELECTED_INSTANCE;
          payload: { attributeSearchName: string; attributeSearchValue: string };
      }
    | {
          type: typeof ContactHistoryTypes.DESELECT_CONTACT_HISTORY_INSTANCE;
          payload: { attributeSearchName: string; attributeSearchValue: string };
      }
    | {
          type: typeof ContactHistoryTypes.SELECT_CH_INTERACTION;
          payload: {
              attributeSearchName: string;
              attributeSearchValue: string;
              selectedInteraction: IInteraction;
          };
      }
    | {
          type: typeof ContactHistoryTypes.ADD_CH_INTERACTION_TRANSCRIPT;
          payload: {
              interactionId: string;
              attributeSearchName: string;
              attributeSearchValue: string;
              transcript: InteractionMessage[];
          };
      }
    | {
          type: typeof ContactHistoryTypes.ADD_CH_AUTOMATED_RULES_AUDIT;
          payload: {
              attributeSearchName: string;
              attributeSearchValue: string;
              interactionId: string;
              automatedRulesAudit: IAutomatedActionAudit[];
          };
      }
    | {
          type: typeof ContactHistoryTypes.ADD_CH_QUEUED_TASKS_AUDIT;
          payload: {
              attributeSearchName: string;
              attributeSearchValue: string;
              interactionId: string;
              queuedTasksAudit: IContactAudit[];
          };
      }
    | {
          type: typeof ContactHistoryTypes.SORT_CH_INTERACTIONS;
          payload: { sort: Sort };
      }
    | {
          type: typeof ContactHistoryTypes.SET_CH_VIEW_PREVIOUS_EMAIL_MESSAGES;
          payload: {
              messageId: string;
              value: boolean;
          };
      }
    | {
          type: typeof ContactHistoryTypes.ADD_CH_TRANSFORMED_ATTACHMENTS;
          payload: {
              attributeSearchName: string;
              attributeSearchValue: string;
              messageId: string;
              transformedAttachments: IEmailAttachment[];
          };
      };

export function addContactHistory(
    attributeSearchName: string,
    attributeSearchValue: string,
    interactions: IInteraction[],
    displayLabel?: string
): ContactHistoryAction {
    return {
        type: ContactHistoryTypes.ADD_CONTACT_HISTORY,
        payload: {
            interactions,
            attributeSearchName,
            attributeSearchValue,
            displayLabel,
        },
    };
}

export function setInteractions(
    attributeSearchName: string,
    attributeSearchValue: string,
    interactions: ContactHistory
): ContactHistoryAction {
    return {
        type: ContactHistoryTypes.SET_CH_INTERACTIONS,
        payload: { attributeSearchName, attributeSearchValue, interactions },
    };
}

export function removeContactHistory(attributeSearchName: string, attributeSearchValue: string): ContactHistoryAction {
    return {
        type: ContactHistoryTypes.REMOVE_CONTACT_HISTORY,
        payload: { attributeSearchName, attributeSearchValue },
    };
}

export function setSelectedInstance(attributeSearchName: string, attributeSearchValue: string): ContactHistoryAction {
    return {
        type: ContactHistoryTypes.SET_CH_SELECTED_INSTANCE,
        payload: { attributeSearchName, attributeSearchValue },
    };
}

export function deselectInstance(attributeSearchName: string, attributeSearchValue: string): ContactHistoryAction {
    return {
        type: ContactHistoryTypes.DESELECT_CONTACT_HISTORY_INSTANCE,
        payload: { attributeSearchName, attributeSearchValue },
    };
}

export function selectInteraction(
    attributeSearchName: string,
    attributeSearchValue: string,
    selectedInteraction: IInteraction
): ContactHistoryAction {
    return {
        type: ContactHistoryTypes.SELECT_CH_INTERACTION,
        payload: { attributeSearchName, attributeSearchValue, selectedInteraction },
    };
}

export function addInteractionTranscript(
    attributeSearchName: string,
    attributeSearchValue: string,
    interactionId: string,
    transcript: InteractionMessage[]
): ContactHistoryAction {
    return {
        type: ContactHistoryTypes.ADD_CH_INTERACTION_TRANSCRIPT,
        payload: { interactionId, transcript, attributeSearchName, attributeSearchValue },
    };
}

export function addAutomatedRulesAudit(
    attributeSearchName: string,
    attributeSearchValue: string,
    interactionId: string,
    automatedRulesAudit: IAutomatedActionAudit[]
): ContactHistoryAction {
    return {
        type: ContactHistoryTypes.ADD_CH_AUTOMATED_RULES_AUDIT,
        payload: { interactionId, automatedRulesAudit, attributeSearchName, attributeSearchValue },
    };
}

export function addQueuedTasksAudit(
    attributeSearchName: string,
    attributeSearchValue: string,
    interactionId: string,
    queuedTasksAudit: IContactAudit[]
): ContactHistoryAction {
    return {
        type: ContactHistoryTypes.ADD_CH_QUEUED_TASKS_AUDIT,
        payload: { interactionId, queuedTasksAudit, attributeSearchName, attributeSearchValue },
    };
}

export function sortInteractions(sort: Sort): ContactHistoryAction {
    return {
        type: ContactHistoryTypes.SORT_CH_INTERACTIONS,
        payload: { sort },
    };
}

export function setViewPreviousEmailMessages(messageId: string, value: boolean): ContactHistoryAction {
    return {
        type: ContactHistoryTypes.SET_CH_VIEW_PREVIOUS_EMAIL_MESSAGES,
        payload: { messageId, value },
    };
}

export function addTransformedAttachments(
    attributeSearchName: string,
    attributeSearchValue: string,
    messageId: string,
    transformedAttachments: IEmailAttachment[]
): ContactHistoryAction {
    return {
        type: ContactHistoryTypes.ADD_CH_TRANSFORMED_ATTACHMENTS,
        payload: { messageId, transformedAttachments, attributeSearchName, attributeSearchValue },
    };
}

export function fetchContactHistoryByChannel(contact: ContactState, channelType: ChannelType) {
    return async (dispatch: ThunkDispatch<RootState, void, ContactHistoryAction>, getState: () => RootState) => {
        const { auth, app, contactHistory, call } = getState();
        const companyID = app.ID;
        const instanceID = app.instance?.ID;
        const token = auth.token;

        const isMonitoringWithoutPhoneNumber = call?.direction === 'monitoring' && !contact.customerEndpointAddress

        if (!instanceID || !token || isMonitoringWithoutPhoneNumber) return;

        const instanceExists = contactHistory.some(
            item => item.attributeSearchName === channelType && item.attributeSearchValue === contact.ID
        );

        if (instanceExists) {
            dispatch(setSelectedInstance(channelType, contact.ID));
            return;
        }

        dispatch(addContactHistory(channelType, contact.ID, []));

        try {
            const queryString = constructQueryStringByChannel(contact, channelType);
            const contactsResponse = queryString ? (await getContacts(companyID, instanceID, token, queryString))?.data : undefined;

            const etrResponse =
                contact?.attributes["sa-sub-channel"] === "SMS-DM"
                    ? await getEventTraceRecords(queryString)
                    : undefined

            if (contactsResponse && channelType === 'TASK') {
                const threadMessageGroups = await getThreadMessageGroups(contactsResponse);
                const otherPartiesContactId = getContactHistoryIdsFromOtherParties(threadMessageGroups, contactsResponse);
                if (otherPartiesContactId.length === 0) {
                    const interactions = transformTaskContacts(contactsResponse, threadMessageGroups);
                    dispatch(setInteractions(channelType, contact.ID, interactions));
                    return;
                }
                const otherPartiesContact = await getContactsById(otherPartiesContactId);
                const contacts = contactsResponse.concat(otherPartiesContact?.data);
                const interactions = transformTaskContacts(contacts, threadMessageGroups);
                dispatch(setInteractions(channelType, contact.ID, interactions));
                return;
            }

            const interactions = mergeAndMapData(contactsResponse, etrResponse);

            if(!interactions) {
                dispatch(setInteractions(channelType, contact.ID, []));
                return;
            }
            dispatch(setInteractions(channelType, contact.ID, interactions));

        } catch (error) {
            console.error('Error getting contact history: ', error);
            dispatch(setInteractions(channelType, contact.ID, []));
        }
    };
}

export function fetchContactHistoryByCustomAttribute(
    attributeSearchName: string,
    attributeSearchValue: string,
    displayLabel?: string
) {
    return async (dispatch: ThunkDispatch<RootState, void, ContactHistoryAction>, getState: () => RootState) => {
        const { auth, app, contactHistory } = getState();
        const companyID = app.ID;
        const instanceID = app.instance?.ID;
        const token = auth.token;

        if (!instanceID || !token) return;

        const instanceExists = contactHistory.some(
            item =>
                item.attributeSearchName === attributeSearchName && item.attributeSearchValue === attributeSearchValue
        );

        if (instanceExists) {
            dispatch(setSelectedInstance(attributeSearchName, attributeSearchValue));
            return;
        }

        dispatch(addContactHistory(attributeSearchName, attributeSearchValue, [], displayLabel));

        try {
            const queryString = paramsToQueryString({ attributeSearchName, attributeSearchValue });
            const response = queryString ? await getContacts(companyID, instanceID, token, queryString) : undefined;
            if (!response) {
                dispatch(setInteractions(attributeSearchName, attributeSearchValue, []));
                return;
            }

            const dataNotTaskChannel: ContactState[] = [];
            const dataIsTaskChannel: ContactState[] = [];
            response.data.forEach(contactState => {
                contactState.channel === 'TASK'
                    ? dataIsTaskChannel.push(contactState)
                    : dataNotTaskChannel.push(contactState);
            });

            if (dataIsTaskChannel.length) {
                const threadMessageGroups = await getThreadMessageGroups(dataIsTaskChannel);
                const otherPartiesContactId = getContactHistoryIdsFromOtherParties(
                    threadMessageGroups,
                    dataIsTaskChannel
                );

                if (otherPartiesContactId.length === 0) {
                    const interactionsNotTasks = transformContacts(dataNotTaskChannel);
                    const interactionsTasks = transformTaskContacts(dataIsTaskChannel, threadMessageGroups);
                    const interactions = sortContactHistoryInteractions(interactionsNotTasks.concat(interactionsTasks), 'date-time-asc');

                    dispatch(setInteractions(attributeSearchName, attributeSearchValue, interactions));
                    return;
                }

                const otherPartiesContact = await getContactsById(otherPartiesContactId);
                const contacts = dataIsTaskChannel.concat(otherPartiesContact?.data);
                const interactionsNotTasks = transformContacts(dataNotTaskChannel);
                const interactionsTasks = transformTaskContacts(contacts, threadMessageGroups);
                const interactions = sortContactHistoryInteractions(interactionsNotTasks.concat(interactionsTasks), 'date-time-asc');

                dispatch(setInteractions(attributeSearchName, attributeSearchValue, interactions));
                return;
            }

            const interactions = transformContacts(response.data);
            dispatch(setInteractions(attributeSearchName, attributeSearchValue, interactions));
        } catch (error) {
            console.error('Error getting contact history: ', error);
            dispatch(setInteractions(attributeSearchName, attributeSearchValue, []));
        }
    };
}

export function fetchContactHistoryInteractionTranscript(
    interaction: IInteraction,
    attributeSearchName: string,
    attributeSearchValue: string
) {
    return async (dispatch: ThunkDispatch<RootState, void, ContactHistoryAction>, getState: () => RootState) => {
        const { auth, app } = getState();
        const companyID = app.ID;
        const instanceID = app.instance?.ID;
        const token = auth.token;

        if (!instanceID || !token) return;

        try {
            const response = await getChatTranscript(companyID, instanceID, token, interaction.ID);
            if (!response) {
                dispatch(addInteractionTranscript(attributeSearchName, attributeSearchValue, interaction.ID, []));
                return;
            }

            const transcripts: connect.ChatTranscriptItem[] = response.data.Transcript;

            const transcriptsAsChatMessages: InteractionMessage[] = [];

            if (transcripts) {
                const transcriptsFilteredAndSorted = transcripts
                    .filter(transcript => transcript?.Type === 'MESSAGE' || transcript?.Type === 'ATTACHMENT')
                    .sort((a, b) => new Date(a.AbsoluteTime).getTime() - new Date(b.AbsoluteTime).getTime());

                transcriptsFilteredAndSorted.forEach(transcript => {
                    const interactionLanguage: string | undefined = interaction.attributes['sa-language'];

                    // Only attempt to transform the content of the transcript if available, as this is not always
                    // the case depending on the transcript type (e.g. MESSAGE transcripts do, ATTACHMENT transcripts do not)
                    const message = transcript.Content
                        ? transformMessageContent(transcript.Content, interactionLanguage)
                        : '';

                    const isSocialInteraction = socialChannels.includes(interaction.channelType);

                    if (isSocialInteraction) {
                        transcriptsAsChatMessages.push({
                            ...transcript,
                            content: message as SocialChatJsonMessage | string,
                        });
                    } else {
                        transcriptsAsChatMessages.push({
                            ...transcript,
                            content: message as SAMessageType | string,
                        });
                    }
                });
            }

            dispatch(
                addInteractionTranscript(
                    attributeSearchName,
                    attributeSearchValue,
                    interaction.ID,
                    transcriptsAsChatMessages
                )
            );
        } catch (error) {
            console.error('Error getting interaction transcript: ', error);
            dispatch(addInteractionTranscript(attributeSearchName, attributeSearchValue, interaction.ID, []));
        }
    };
}

export function fetchAutomatedRulesAudit(
    attributeSearchName: string,
    attributeSearchValue: string,
    interaction: IInteraction
) {
    return async (dispatch: ThunkDispatch<RootState, void, ContactHistoryAction>, getState: () => RootState) => {
        const { app, auth } = getState();
        const companyID = app.ID;
        const instanceID = app.instance?.ID;
        const token = auth.token;
        if (!instanceID || !token) return;

        const emailMessage = interaction?.emailMessages?.find(
            emailMessage => emailMessage.messageId === interaction.messageId
        );

        if (!emailMessage?.actionAuditId) {
            dispatch(addAutomatedRulesAudit(attributeSearchName, attributeSearchValue, interaction.ID, []));
            return;
        }

        try {
            const response = await getActionAudit(companyID, instanceID, token, emailMessage.actionAuditId);
            if (!response) {
                dispatch(addAutomatedRulesAudit(attributeSearchName, attributeSearchValue, interaction.ID, []));
                return;
            }

            dispatch(addAutomatedRulesAudit(attributeSearchName, attributeSearchValue, interaction.ID, response.data));
        } catch (error) {
            console.error('Error getting automated rules audit data: ', error);
            dispatch(addAutomatedRulesAudit(attributeSearchName, attributeSearchValue, interaction.ID, []));
        }
    };
}

export function fetchQueuedTasksAudit(
    attributeSearchName: string,
    attributeSearchValue: string,
    interaction: IInteraction
) {
    return async (dispatch: ThunkDispatch<RootState, void, ContactHistoryAction>, getState: () => RootState) => {
        const { auth, app } = getState();
        const companyID = app.ID;
        const instanceID = app.instance?.ID;
        const token = auth.token;
        if (!instanceID || !token) return;

        try {
            const response = await getContactAudit(companyID, instanceID, token, interaction.ID);
            if (!response) {
                dispatch(addQueuedTasksAudit(attributeSearchName, attributeSearchValue, interaction.ID, []));
                return;
            }

            dispatch(addQueuedTasksAudit(attributeSearchName, attributeSearchValue, interaction.ID, response.data));
        } catch (error) {
            console.error('Error getting queued tasks audit data: ', error);
            dispatch(addQueuedTasksAudit(attributeSearchName, attributeSearchValue, interaction.ID, []));
        }
    };
}

export function fetchAttachmentUrls(
    attributeSearchName: string,
    attributeSearchValue: string,
    attachments: IEmailAttachment[],
    messageId: string,
    threadId: string
) {
    return async (dispatch: ThunkDispatch<RootState, void, ContactHistoryAction>, getState: () => RootState) => {
        const { auth, app } = getState();
        const instanceID = app.instance?.ID;
        const token = auth.token;
        if (!instanceID || !token) return;

        try {
            const newAttachments: IEmailAttachment[] = await Promise.all(
                attachments.map(async attachment => {
                    const { s3Key, filename } = attachment;
                    let url = '';
                    try {
                        url = (await getDownloadURL(threadId, messageId, encodeURI(s3Key))).url;
                    } catch (error) {
                        console.error(`Error retrieving URL for ${s3Key}/${filename}: ${error}`);
                    }

                    return { ...attachment, s3URL: url };
                })
            );
            dispatch(addTransformedAttachments(attributeSearchName, attributeSearchValue, messageId, newAttachments));
        } catch (error) {
            console.error('Error getting attachment S3 urls:', error);
            dispatch(addTransformedAttachments(attributeSearchName, attributeSearchValue, messageId, attachments));
        }
    };
}

export function fetchContactHistoryContact(
    attributeSearchName: string,
    attributeSearchValue: string,
    interaction: IInteraction
) {
    return async (dispatch: ThunkDispatch<RootState, void, ContactHistoryAction>) => {
        try {
            const fullInteraction = await getContact(interaction.ID);
            dispatch(
                selectInteraction(attributeSearchName, attributeSearchValue, {
                    ...interaction,
                    ...fullInteraction,
                })
            );
        } catch (error) {
            console.error('Error getting interaction:', error);
            dispatch(selectInteraction(attributeSearchName, attributeSearchValue, interaction));
        }
    };
}
